import {ChangeDetectorRef, Component, HostListener} from '@angular/core';
import {UserCardService} from "../../service/user-card.service";
import {UserCardResponse} from "../../interface/userCardResponse";
import {UserCardResponseWrap} from "../../interface/userCardResponseWrap";
import {UserResponse} from "../../interface/userResponse";
import {UserService} from "../../service/user.service";
import {ImageType} from "../../enums/image-type-enum";
import {SnackBarUtil} from "../../qr-copy-message/snack-bar-util.service";
import {MockupDataModel} from "../../qr-copy-message/mockup-data.model";
import {MatDialog, MatDialogConfig} from "@angular/material/dialog";
import {QrGenerateComponent} from "../../qr-generate/qr-generate.component";
import {DeleteProfileCardComponent} from "../../profile-card/delete-profile-card/delete-profile-card.component";
import {UserCardUsernameEditComponent} from "../user-card-username-edit/user-card-username-edit.component";
import {HttpErrorResponse} from "@angular/common/http";
import {saveAs} from "file-saver";
import {GetPageUtil} from "../get-page-util";
import {TranslateService} from "@ngx-translate/core";
import {CommissionService} from "../../service/commission.service";
import {CommissionTemplate} from "../../interface/commission-template.interface";
import {UsersFilterComponent} from "./users-filter/users-filter.component";
import {UserFilter} from "../../interface/user-filter.interface";

@Component({
  selector: 'app-users-cards',
  templateUrl: './users-cards.component.html',
  styleUrls: ['./users-cards.component.scss', './users-cards.component-media.scss']
})
export class UsersCardsComponent {

  private readonly REMOVE_USER_TITLE = "Remove the user?";
  private readonly REMOVE_USER_DESCRIPTION = "You will lose all the information associated with this user.";
  private readonly DELETING_USER_WITH_BALANCE_ERROR_MESSAGE = 'User cannot be deleted as they have a balance in their account.';
  private readonly DELETING_USER_UNEXPECTED_ERROR = 'An error occurred while deleting the user. Please try again.';
  private readonly DELETING_USER_WITH_BALANCE_RESPONSE_MESSAGE = 'delete a user who has money in his balance';

  userPage: number = 0;
  userSize: number = 9;
  totalUsersPageCount: number | any;
  totalUsersCount: number | any;
  isLoading: boolean = false;

  currentCardPage: number = 0;
  cardPageSize: number = 9;
  totalElementCount: number | any;

  users: UserResponse[] = [];
  oldUsers: UserResponse[] = [];
  searchUsers: UserResponse[] = [];
  cards: UserCardResponse[] = [];
  wrappedCards: UserCardResponseWrap[] = [];

  isAllUsersLoaded: boolean = false;

  profileImageUrl: string = '';

  usersCount: bigint = BigInt(0);
  userCardsCount: bigint = BigInt(0);

  isLoadingNewData: boolean = false;
  searchQuery: string = '';
  trimmedQuery: string = '';
  templates: CommissionTemplate[] = [];
  activeFilter: UserFilter | null = null;
  private getPageUtil = new GetPageUtil();

  constructor(
    private userCardService: UserCardService,
    public userService: UserService,
    private snackBar: SnackBarUtil,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private commissionService: CommissionService
  ) {
    userService.getUsersCount().subscribe((usersCount) => this.usersCount = usersCount);
    userCardService.getUserCardsCount().subscribe((cardsCount) => this.userCardsCount = cardsCount)
    this.getAllCards(this.currentCardPage, this.cardPageSize)
    this.getAllUsers()
    commissionService.getAllTemplates().subscribe(templates => this.templates = templates);
  }

  get isFilterActive(): boolean {
    return this.activeFilter !== null;
  }

  openFilterDialog(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = 'filter-dialog';
    dialogConfig.data = this.activeFilter;
    const dialogRef = this.dialog.open(UsersFilterComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((result: UserFilter | null | undefined) => {
      if (result === undefined) return;
      this.activeFilter = result;
      this.userPage = 0;
      this.getAllUsers();
    });
  }

  getAllUsers() {
    this.isLoadingNewData = true;
    const request$ = this.activeFilter
      ? this.userService.filterUsers(this.userPage, this.userSize, this.activeFilter)
      : this.userService.findAll(this.userPage, this.userSize);
    request$.subscribe({
        next: (response) => {
          this.users = response.content;
          this.oldUsers = this.users
          for (const user of this.users) {
            user.showDetail = false
            user.userCardsResponse = (user.userCards || []).map(card => ({
              userCardResponse: card,
              isChecked: false
            }));
          }
          this.totalUsersCount = response.totalElements;
          this.totalUsersPageCount = Math.ceil(response.totalElements / this.userSize);
        },
        complete: () => {
          this.isLoadingNewData = false;
        }
      }
    )
  }

  getAllCards(currentCardPage: number, cardPageSize: number) {
    this.userCardService.getAllUserCards(currentCardPage, cardPageSize).subscribe(
      (response) => {
        this.cards = response.content;
        this.wrappedCards = this.cards.map(card => ({
          userCardResponse: card
        }));
        this.totalElementCount = response.totalElements;
      }
    )
  }

  loadMoreUsers() {
    if (this.userPage < this.totalUsersPageCount - 1) {
      if (this.trimmedQuery) {
        this.search(true)
      } else {
        this.getAllUsers();
      }
      if (this.userPage >= this.totalUsersPageCount - 1) {
        this.isAllUsersLoaded = true;
      }
    }
  }

  editUsername(event: { id: bigint, cardId: string }) {
    const {id, cardId} = event;
    const card = this.cards.find(card => card.id === id);
    this.users.forEach(user => {
      if (user.userCardsResponse) {
        const card = user.userCardsResponse.find((card) => card.userCardResponse.id === id);
        if (card) {
          card.userCardResponse.cardId = cardId;
          card.userCardResponse.qrUsername = cardId;
          this.cdr.detectChanges();
        }
      }
    });
  }

  openDeleteCardDialog(user: UserResponse) {
    const dialogRef = this.dialog.open(DeleteProfileCardComponent, {
      data: {
        deleteTitle: this.REMOVE_USER_TITLE,
        deleteDescription: this.REMOVE_USER_DESCRIPTION,
      }
    });

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (result) {
          this.deleteUser(user);
        }
      }
    })
  }

  deleteUser(user: UserResponse) {
    if (!user || !user.id) return;
    const id: bigint = user.id;
    this.userService.deleteById(id).subscribe({
      next: () => {
        this.deleteUserFromListById(id);
        this.openSnackBar(MockupDataModel.success(`User ${user.name} ${user.surname} was successfully deleted.`), SnackBarUtil.CLASSES.success);
      },
      error: (err: HttpErrorResponse) => {
        if (err.error?.message && err.error.message.includes(this.DELETING_USER_WITH_BALANCE_RESPONSE_MESSAGE)) {
          this.openSnackBar(MockupDataModel.error(this.DELETING_USER_WITH_BALANCE_ERROR_MESSAGE), SnackBarUtil.CLASSES.error);
        } else {
          this.openSnackBar(MockupDataModel.error(this.DELETING_USER_UNEXPECTED_ERROR), SnackBarUtil.CLASSES.error);
        }
      }
    })
  }

  getPageNumbers(): number[] {
    return this.getPageUtil.getPageNumbers(this.userPage, this.totalUsersCount, this.userSize);
  }

  search(isLoadMore?: boolean) {
    this.trimmedQuery = this.searchQuery.trim();
    if (this.trimmedQuery.length > 2) {
      if (!isLoadMore) {
        this.userPage = 0;
      }
      this.userService.search(this.trimmedQuery, this.userPage, this.userSize).subscribe({
        next: (response) => {
          if (isLoadMore) {
            this.users = [...this.searchUsers, ...response.content];
          } else {
            this.users = response.content;
            this.searchUsers = this.users
          }
          this.totalUsersPageCount = Math.ceil(response.totalElements / this.userSize);
          for (const user of this.users) {
            user.showDetail = false
            user.userCardsResponse = (user.userCards || []).map(card => ({
              userCardResponse: card,
              isChecked: false
            }));
          }
        }
      });
    } else if (!this.trimmedQuery.length) {
      this.userPage = 0;
      this.users = this.oldUsers;
    }
  }

  downloadUsers() {
    this.userService.downloadUser().subscribe({
      next: (blob: Blob) => {
        saveAs(blob, this.getFilename());
      }
    })
  }

  goToPage(page: number) {
    if (page >= 0 && page < this.totalUsersPageCount) {
      this.userPage = page;
      if (this.trimmedQuery) {
        this.search();
      } else {
        this.getAllUsers();
      }
    }
  }

  toggleBusiness(event: Event, card: UserCardResponse, userId: bigint): void {
    event.stopPropagation();
    const newStatus = !card.isBusiness;
    this.userCardService.updateIsBusiness(userId, card.cardId, newStatus).subscribe({
      next: () => {
        card.isBusiness = newStatus;
      },
      error: () => {
        this.openSnackBar(
          MockupDataModel.error('Failed to update business status.'),
          SnackBarUtil.CLASSES.error
        );
      }
    });
  }

  showQRCode(event: Event, card: UserCardResponse): void {
    event.stopPropagation();
    this.userCardService.generateQRCode(card.id).subscribe((blob: Blob) => {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.data = {
        userCard: card,
        qrCodeUrl: URL.createObjectURL(blob)
      };
      dialogConfig.panelClass = 'qr-dialog';
      this.dialog.open(QrGenerateComponent, dialogConfig);
    });
  }

  openEditUsernameDialog(event: Event, card: UserCardResponse): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(UserCardUsernameEditComponent, {
      data: {cardId: card.cardId, id: card.id}
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        card.cardId = result.cardId;
        card.qrUsername = result.cardId;
      }
    });
  }

  onTemplateChange(card: UserCardResponse, templateId: number): void {
    this.commissionService.assignCards(templateId, [Number(card.id)]).subscribe({
      next: () => {
        const tpl = this.templates.find(t => t.id === templateId);
        if (tpl) card.commissionTemplateName = tpl.name;
      },
      error: () => {
        this.openSnackBar(
          MockupDataModel.error('Failed to update commission template.'),
          SnackBarUtil.CLASSES.error
        );
      }
    });
  }

  private getFilename(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const day = today.getDate().toString().padStart(2, '0');
    return `users_list_${year}${month}${day}.xlsx`;
  }

  private deleteUserFromListById(id: bigint) {
    this.users = this.users.filter(user => user.id !== id);
  }

  private openSnackBar(data: MockupDataModel, classList: string[]) {
    this.snackBar.openSnackBar({
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: classList,
      data: data
    });
  }

  protected readonly ImageType = ImageType;

  public translateSubscriptionTitle(title: string | null | undefined): string {
    if (!title) return '';
       const titleMap: { [key: string]: string } = {
      'Starter': 'subscription_plans.starter',
      'Standard': 'subscription_plans.standard',
      'Premium': 'subscription_plans.premium',
      'Basic': 'subscription_plans.basic',
      'Professional': 'subscription_plans.professional',
      'Unlim': 'subscription_plans.unlim',
      'Free': 'subscription_plans.free'
    };

    const translationKey = titleMap[title];
    return translationKey ? this.translateService.instant(translationKey) : title;
  }
}
